/**
 * @file        src/pages/flight/BookingList.jsx
 * @description B2C Flight booking list over flight_bookings. Mirrors the legacy
 *              console: Success/Failed/Pending summary cards (click to filter),
 *              rich itinerary columns (journey date, segment, PNR, passenger,
 *              fare+status) and per-row actions (Details / Invoice / Update /
 *              Cancel).
 * @author      Kinetika Labs
 * @created     2026-07-01
 * @changelog
 *   - 2026-10-09 Claude — A new ?open= while mounted opens that drawer (global search).
 *   - 2026-09-23 Claude — Document links carry the screen they were opened from
 *                          (lib/docOrigin), so a corporate booking's invoice /
 *                          e-ticket keeps the Corporate group open in the
 *                          sidebar and its Back button returns to THIS list.
 *   - 2026-08-10 Claude — Row actions follow the docAccess() rule: Tax Invoice /
 *                          E-Ticket / Share only exist for the statuses the
 *                          client approved (pending & cancellation-pending →
 *                          none, confirmed → all, cancelled → invoice only).
 *   - 2026-07-01 Claude — Initial (B2C Flight Management).
 *   - 2026-07-09 Claude — Booking ID + Guest customer + row actions.
 *   - 2026-07-09 Claude — Summary filter cards + itinerary columns (parity with
 *                          the legacy Flight Booking List).
 *   - 2026-07-27 Claude — Accept title/subtitle/defaultAudience/lockAudience
 *                          props so CorporateBookingList.jsx can reuse this
 *                          component fixed to audience=b2b instead of forking it.
 *   - 2026-08-03 Claude — Brand filter is registry-driven (BrandSelect), not the
 *                          hardcoded Neon/Beyond pair.
 *   - 2026-08-04 Claude — CR D3/D4: dense rows + sticky header (ReadOnlyListPage
 *                          `dense`), right-aligned Fare, the Trace ID folded
 *                          into the PNR cell (one less column), and the 4-5
 *                          row buttons collapsed into one RowMenu. Added a
 *                          booking-date range, a travel-date range and a
 *                          passenger/contact search, all reflected in the URL
 *                          via useSearchParams so a filtered view is a
 *                          shareable link (status/audience/brand/dates/
 *                          passenger — NOT page/pageSize or the Ref/PNR search
 *                          box, which stay ReadOnlyListPage-local, matching
 *                          how status/audience/brand already behaved before
 *                          this change).
 *   - 2026-08-04 Claude — Agent retirement (CR E8): removed the conditional
 *                          Commission column (b2b_commission) from the
 *                          Corporate Booking List — new bookings no longer
 *                          record commission; historical values are still in
 *                          the DB but no longer surfaced here.
 *   - 2026-08-04 Claude — CR D2: added a "Share" (email icon) row action
 *                          opening ShareEmailModal (Ticket/Invoice/Both, real
 *                          PDF attachment, logged).
 *   - 2026-09-15 Claude — The audience PICKER is gone and `audience` is no longer a
 *                          URL filter. This B2C-labeled screen offered a "Customer
 *                          type" dropdown whose options included "B2B / Corporate"
 *                          and "All (B2C + B2B)", so corporate bookings could be
 *                          pulled into the B2C screen (and "All" sent
 *                          `audience=all`, which the API's b2c|b2b enum rejects
 *                          outright). B2C and Corporate are SEPARATE screens, not
 *                          two filter values of one screen: the audience is now
 *                          fixed by the component's `audience` prop, is always sent
 *                          to the API, and cannot be overridden from the query
 *                          string — so a shared link cannot cross the boundary
 *                          either. `defaultAudience`/`lockAudience` are replaced by
 *                          the single `audience` prop.
 *   - 2026-08-10 Claude — Defaults to audience=b2c instead of the unfiltered
 *                          "All" view — this B2C-labeled screen was showing B2B
 *                          bookings mixed in by default even though a dedicated
 *                          Corporate Booking List already exists. "All" is now
 *                          an explicit dropdown choice (value 'all', not '') so
 *                          it round-trips through the URL distinctly from "no
 *                          filter chosen yet".
 *   - 2026-09-15 Claude — Passenger filter capped at 120 chars, matching the route.
 *                          (A parallel "All (B2C + B2B)" fix was dropped at merge:
 *                          the picker it patched no longer exists.)
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReadOnlyListPage, { fmtDate } from './ReadOnlyListPage';
import Chip from '../../components/Chip';
import Icon from '../../components/Icon';
import BrandSelect from '../../components/BrandSelect';
import DatePicker from '../../components/DatePicker';
import RowMenu from '../../components/RowMenu';
import ShareEmailModal from '../../components/ShareEmailModal';
import { get } from '../../api/client';
import { money, customerCell, docAccess } from './cells';
import { docPath } from '../../lib/docOrigin';
import BookingDetailsDrawer from './BookingDetailsDrawer';
import UpdateBookingModal from './UpdateBookingModal';
import CancelBookingModal from './CancelBookingModal';

const statusTone = (s) => (/cancel|fail/i.test(s) ? 'rej' : /pend|hold|initiat|await/i.test(s) ? 'pend' : 'ok');
const day = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const CARDS = [
  { key: 'success', label: 'Success', tone: 'card-ok' },
  { key: 'failed', label: 'Failed', tone: 'card-rej' },
  { key: 'pending', label: 'Pending', tone: 'card-pend' },
  { key: 'cancellation_pending', label: 'Cancellation Pending', tone: 'card-pend' },
  { key: 'cancelled', label: 'Cancelled', tone: 'card-rej' },
];

// Filters kept in the URL (D4) — everything a shared link should reproduce.
// page/pageSize and the Ref/PNR search box stay ReadOnlyListPage-local.
// The booking screens are audience-SEPARATED, not audience-filtered: /flight/bookings
// and /hotel/bookings are B2C-only and the Corporate Booking Lists are B2B-only.
// `audience` is therefore NOT a URL filter — it is fixed by the screen, cannot be
// overridden from the query string, and has no picker.
const URL_KEYS = ['status', 'brand', 'bookingFrom', 'bookingTo', 'travelFrom', 'travelTo', 'passenger'];

export default function BookingList({
  title = 'Booking List',
  subtitle = 'B2C Flight · flight bookings',
  // Which audience this SCREEN is. Not a default the admin can change — see the
  // note above URL_KEYS.
  audience = 'b2c',
} = {}) {
  const [updateRow, setUpdateRow] = useState(null);
  const [cancelRow, setCancelRow] = useState(null);
  const [shareRow, setShareRow] = useState(null);

  const [params, setParams] = useSearchParams();

  // ?open=<booking id> lifts this booking's details drawer immediately — the
  // link the Action Centre sends for a refund, so "work the refund" lands on the
  // refund pipeline instead of on a list with the reference still to find.
  // Seeded ONCE: after that the drawer is ordinary state, and closing it clears
  // the param so a refresh does not reopen what was just dismissed. `open` is
  // deliberately absent from URL_KEYS — it selects a row, it does not filter the
  // list, so it must not reach the API or light up the "filters active" chip.
  const [detailsId, setDetailsId] = useState(() => {
    const v = Number(params.get('open'));
    return Number.isInteger(v) && v > 0 ? v : null;
  });
  // A new ?open= while this screen is already mounted (the global search) opens
  // that booking too — the seed above only runs on the first render.
  const openParam = params.get('open');
  useEffect(() => {
    const v = Number(openParam);
    if (Number.isInteger(v) && v > 0) setDetailsId(v);
  }, [openParam]);
  const closeDetails = () => {
    setDetailsId(null);
    if (params.get('open')) setFilters({ open: '' });
  };
  const status = params.get('status') || null;

  const brand = params.get('brand') || '';
  const bookingFrom = params.get('bookingFrom') || '';
  const bookingTo = params.get('bookingTo') || '';
  const travelFrom = params.get('travelFrom') || '';
  const travelTo = params.get('travelTo') || '';
  const passenger = params.get('passenger') || '';

  /** Merge one or more filter changes into the URL in a single navigation. */
  function setFilters(patch) {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v); else next.delete(k);
    }
    setParams(next, { replace: true });
  }

  // The stat cards belong to THIS screen's audience. Counting every booking made
  // the B2C screen advertise corporate volume (and vice-versa), and since a card
  // is also a status filter, "Booked: 120" then listed a different number of rows.
  const { data: sum } = useQuery({
    queryKey: ['flight-bookings', 'summary', audience],
    queryFn: () => get('/flight/bookings/summary', { params: { audience } }),
  });
  const counts = sum?.data || {};

  // The screen's audience always goes to the API; the rest are the admin's filters.
  const extraParams = { audience };
  for (const k of URL_KEYS) {
    const v = params.get(k) || '';
    if (v) extraParams[k] = v;
  }

  const anyFilterActive = URL_KEYS.some((k) => params.get(k));

  const toolbar = (
    <>
      <div className="stat-row">
        {CARDS.map((c) => (
          <button
            key={c.key}
            className={`stat-card ${c.tone}${status === c.key ? ' active' : ''}`}
            onClick={() => setFilters({ status: status === c.key ? '' : c.key })}
            title={`Filter ${c.label}`}
          >
            <span className="stat-num">{counts[c.key] ?? '—'}</span>
            <span className="stat-label">{c.label}</span>
          </button>
        ))}
        {anyFilterActive ? (
          <button className="btn danger" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
            <Icon name="x" /> Clear Filter
          </button>
        ) : null}
      </div>
      <div className="search-row" style={{ marginTop: 8 }}>
        <div className="field">
          <label>Brand</label>
          <BrandSelect value={brand} onChange={(v) => setFilters({ brand: v })} withAll />
        </div>
      </div>
      <div className="filter-row">
        <div className="field">
          <label>Booking date from</label>
          <DatePicker value={bookingFrom} onChange={(v) => setFilters({ bookingFrom: v })} />
        </div>
        <span className="date-sep">–</span>
        <div className="field">
          <label>Booking date to</label>
          <DatePicker value={bookingTo} onChange={(v) => setFilters({ bookingTo: v })} min={bookingFrom} />
        </div>
        <div className="field">
          <label>Travel date from</label>
          <DatePicker value={travelFrom} onChange={(v) => setFilters({ travelFrom: v })} />
        </div>
        <span className="date-sep">–</span>
        <div className="field">
          <label>Travel date to</label>
          <DatePicker value={travelTo} onChange={(v) => setFilters({ travelTo: v })} min={travelFrom} />
        </div>
        <div className="field grow">
          <label>Passenger name / contact</label>
          <input
            value={passenger}
            maxLength={120}
            onChange={(e) => setFilters({ passenger: e.target.value })}
            placeholder="Search by traveller name, email…"
          />
        </div>
      </div>
    </>
  );

  const rowActions = (row) => {
    // Invoice / E-Ticket / Share follow the client-approved status rule — see
    // lib/docAccess. Details (the cart view) and Update are admin tools and stay.
    const docs = docAccess(row.booking_status);
    return (
      <RowMenu
        label="Actions"
        items={[
          { key: 'details', label: 'Details', icon: 'page', onClick: () => setDetailsId(row.id) },
          { key: 'invoice', label: 'Tax Invoice', icon: 'download', hidden: !docs.invoice, to: docPath(`/flight/bookings/${row.id}/invoice`, audience) },
          { key: 'ticket', label: 'E-Ticket', icon: 'page', hidden: !docs.voucher, to: docPath(`/flight/bookings/${row.id}/ticket`, audience) },
          { key: 'update', label: 'Update', icon: 'edit', onClick: () => setUpdateRow(row) },
          { key: 'share', label: 'Share', icon: 'mail', hidden: !docs.share, onClick: () => setShareRow(row) },
          {
            key: 'cancel', label: 'Cancel', icon: 'x', danger: true,
            hidden: /cancel|refund/i.test(row.booking_status || ''),
            onClick: () => setCancelRow(row),
          },
        ]}
      />
    );
  };

  return (
    <>
      <ReadOnlyListPage
        title={title}
        subtitle={subtitle}
        endpoint="/flight/bookings"
        queryRoot="flight-bookings"
        searchLabel="Booking Ref / PNR"
        actionsLabel="Action"
        rowActions={rowActions}
        toolbar={toolbar}
        extraParams={Object.keys(extraParams).length ? extraParams : undefined}
        showIndex={false}
        dense
        columns={[
          {
            key: 'pnr_code',
            label: 'Booking',
            render: (v, r) => (
              <div>
                <div className="mono"><b>{v || '—'}</b></div>
                <div className="muted" style={{ fontSize: '.8em' }} title={`Trace ID ${r.id}`}>
                  #{r.id} · {fmtDate(r.created_at)}
                </div>
              </div>
            ),
          },
          {
            key: 'journey_from',
            label: 'Journey Date',
            render: (v, r) => (
              <div>
                <div>D: {day(v)}</div>
                {r.journey_return ? <div className="muted">R: {day(r.journey_return)}</div> : null}
              </div>
            ),
          },
          {
            key: 'segment',
            label: 'Segment',
            render: (v, r) => (
              <div>
                <div className="mono">{v || '—'}</div>
                <div className="muted" style={{ fontSize: '.8em' }}>{r.trip_type || 'One-way'}</div>
              </div>
            ),
          },
          { key: 'booking_status', label: 'Status', render: (v) => v ? <Chip tone={statusTone(v)}>{v}</Chip> : '—' },
          { key: 'lead_passenger', label: 'Passenger', render: (v) => v || '—' },
          { key: 'customer_name', label: 'Customer', render: customerCell },
          {
            key: 'total_paid',
            label: 'Fare',
            align: 'right',
            render: (v) => <b>{money(v)}</b>,
          },
        ]}
      />

      {detailsId ? (
        <BookingDetailsDrawer
          bookingId={detailsId}
          audience={audience}
          onClose={closeDetails}
          onUpdate={(b) => { closeDetails(); setUpdateRow(b); }}
          onCancel={(b) => { closeDetails(); setCancelRow(b); }}
        />
      ) : null}
      {updateRow ? <UpdateBookingModal booking={updateRow} onClose={() => setUpdateRow(null)} /> : null}
      {cancelRow ? <CancelBookingModal booking={cancelRow} onClose={() => setCancelRow(null)} /> : null}
      {shareRow ? <ShareEmailModal kind="flight" booking={shareRow} onClose={() => setShareRow(null)} /> : null}
    </>
  );
}
